/**
 * Base API client for user-service
 */

import { getUserServiceApiBase } from './userServiceBaseUrl';
import { handleSessionExpired } from '../auth/session-expired';

export interface ApiError {
  error: string;
  status?: number;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Base fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${getUserServiceApiBase()}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const raw = await response.text();
  let data: Record<string, unknown> = {};

  if (!response.ok) {
    if (raw.length > 0) {
      try {
        data = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        data = { error: raw.slice(0, 300) };
      }
    }
    throw new ApiClientError(
      (typeof data.error === 'string' && data.error) ||
        response.statusText ||
        'Request failed',
      response.status,
      data,
    );
  }

  // No body (e.g. 204) — valid for some APIs; avoid masking as `{}` from failed JSON.parse.
  if (response.status === 204 || response.status === 205) {
    return {} as T;
  }

  if (raw.length === 0) {
    throw new ApiClientError(
      'Empty response from server (expected JSON). Confirm the request hits user-service (e.g. POST /api/users/:id/hard-delete) and the route is deployed.',
      response.status,
      {},
    );
  }

  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    throw new ApiClientError(
      `Invalid JSON in success response (status ${response.status})`,
      response.status,
      { raw: raw.slice(0, 200) },
    );
  }

  return data as T;
}

function authenticatedFetchBase<T>(
  endpoint: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<T> {
  return apiFetch<T>(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Authenticated fetch that automatically refreshes the access token on 401 and retries once.
 * If refresh fails, clears the session and redirects to login immediately.
 */
export async function authenticatedFetch<T>(
  endpoint: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<T> {
  try {
    return await authenticatedFetchBase<T>(endpoint, accessToken, options);
  } catch (err) {
    if (!(err instanceof ApiClientError) || err.status !== 401) {
      throw err;
    }

    let newToken: string | null = null;
    try {
      const { refreshAccessTokenForUserService } = await import('./user-service-auth');
      newToken = await refreshAccessTokenForUserService();
    } catch {
      newToken = null;
    }

    if (!newToken) {
      handleSessionExpired();
      throw err;
    }

    return authenticatedFetchBase<T>(endpoint, newToken, options);
  }
}

export { apiFetch };
